import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageShell } from "../components/Layout";
import SEO from "../components/SEO";
import CelebrityAvatar from "../components/CelebrityAvatar";
import api from "../lib/api";
import { startGoogleSignIn } from "../lib/capacitorAuth";
import { Crown, Sparkle, MagnifyingGlass, ArrowRight, Gift, FlowerLotus, CrownSimple, Lightning, LockKey } from "@phosphor-icons/react";

const RATING_STYLE = {
  AA: { bg: "bg-emerald-500/15", text: "text-emerald-300", border: "border-emerald-500/40" },
  A:  { bg: "bg-sky-500/15",     text: "text-sky-300",     border: "border-sky-500/40" },
  B:  { bg: "bg-amber-500/15",   text: "text-amber-300",   border: "border-amber-500/40" },
  X:  { bg: "bg-zinc-500/15",    text: "text-zinc-300",    border: "border-zinc-500/40" },
};

const CATEGORY_ICON = { Crown, Sparkle, GiftIcon: Gift, FlowerLotus, CrownSimple, Lightning };
const CATEGORY_GRADIENT = {
  billionaires:    "from-[#F59E0B] via-[#FFB800] to-[#F97316]",
  "rags-to-riches":"from-[#8B5CF6] via-[#A855F7] to-[#EC4899]",
  "lottery-winners":"from-[#10B981] via-[#14B8A6] to-[#06B6D4]",
  "spiritual-gurus":"from-[#6366F1] via-[#8B5CF6] to-[#D946EF]",
  "royals":         "from-[#7C2D12] via-[#B45309] to-[#FCD34D]",
  "internet-famous":"from-[#EC4899] via-[#F97316] to-[#FACC15]",
};

// ─── Global celebrity search bar (used on the hub) ──────────────────────
function CelebritySearchBar() {
  // Debounced typeahead against /api/celebrities/search. Live dropdown
  // shows up to 8 matches with avatar + category pill. Enter or click
  // navigates to the celebrity detail page (which is under
  // /celebrities/{category}/{slug} — not /celebrities/detail/{slug} —
  // because the list-view route pattern is what the app is wired to).
  const [q, setQ] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [needAuth, setNeedAuth] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [activeIdx, setActiveIdx] = React.useState(-1);
  const wrapRef = React.useRef(null);

  // Debounce fetch — 220ms is fast enough to feel live but rare enough
  // that the scrape-guard rate limit never trips on a real typing user.
  React.useEffect(() => {
    const query = q.trim();
    if (query.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get("/celebrities/search", { params: { q: query, limit: 8 } })
        .then(({ data }) => setResults(data.results || []))
        .catch((err) => {
          // 401 = members-only database (Oct 2026 anti-scrape policy)
          if (err?.response?.status === 401) setNeedAuth(true);
          setResults([]);
        })
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  // Close the dropdown when clicking outside.
  React.useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const go = (celeb) => {
    if (!celeb?.slug) return;
    // Reset local UI then navigate — using href to work regardless of
    // whether we're inside a react-router context on the page level.
    setOpen(false);
    setQ("");
    setResults([]);
    window.location.href = `/celebrities/${celeb.category}/${celeb.slug}`;
  };

  const onKey = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[activeIdx >= 0 ? activeIdx : 0]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div
      ref={wrapRef}
      className="mt-7 relative z-50 max-w-2xl"
      data-testid="celeb-search-wrap"
    >
      <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[#FFB800]/30 bg-white/70 backdrop-blur-sm focus-within:border-[#FFB800] focus-within:shadow-[0_4px_18px_rgba(255,184,0,0.15)] transition-all">
        <MagnifyingGlass size={18} weight="bold" className="text-gold flex-shrink-0" />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setActiveIdx(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="Search a celebrity by name, company or industry — e.g. Elon, Ambani, Queen…"
          className="flex-1 bg-transparent outline-none text-sm sm:text-base placeholder:text-stone-400"
          data-testid="celeb-search-input"
          autoComplete="off"
        />
        {loading && <span className="text-[11px] text-stone-500">searching…</span>}
      </div>

      {open && q.trim().length >= 2 && (
        <div
          className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl border border-[#FFB800]/25 shadow-xl overflow-hidden max-h-[420px] overflow-y-auto"
          data-testid="celeb-search-results"
        >
          {needAuth && results.length === 0 && !loading ? (
            <button
              type="button"
              onClick={() => startGoogleSignIn(window.location.href)}
              className="w-full px-4 py-5 text-center"
              data-testid="celeb-search-signin"
            >
              <span className="text-sm font-medium text-[#3F2D1B]">Sign in free to search the celebrity database</span>
              <span className="block text-[11px] text-stone-500 mt-0.5">Members-only research — one tap with Google</span>
            </button>
          ) : (
            <>
              {results.length === 0 && !loading && (
                <div className="px-4 py-6 text-center text-sm text-stone-500" data-testid="celeb-search-empty">
                  No celebrity matched <span className="italic">"{q}"</span>. Try a fuller name.
                </div>
              )}
              {results.map((c, idx) => (
            <button
              key={c.slug}
              type="button"
              onMouseEnter={() => setActiveIdx(idx)}
              onClick={() => go(c)}
              className={`w-full text-left px-4 py-2.5 flex items-center gap-3 border-b border-stone-100 last:border-b-0 transition-colors ${
                idx === activeIdx ? "bg-[#FFB800]/10" : "hover:bg-stone-50"
              }`}
              data-testid={`celeb-search-hit-${c.slug}`}
            >
              <CelebrityAvatar
                src={c.photo}
                name={c.name}
                size={40}
                className="ring-2 ring-white shadow"
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#3F2D1B] truncate">{c.name}</div>
                <div className="text-[11px] text-stone-500 truncate">
                  {c.company || c.industry || c.country}
                </div>
              </div>
              <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-stone-100 text-stone-700 flex-shrink-0 uppercase">
                {(c.category || "").replace(/-/g, " ")}
              </span>
              {c.rodden_rating && (
                <span
                  className={`text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded flex-shrink-0 ${
                    c.rodden_rating === "AA" ? "bg-emerald-100 text-emerald-800" :
                    c.rodden_rating === "A"  ? "bg-sky-100 text-sky-800" :
                    c.rodden_rating === "B"  ? "bg-amber-100 text-amber-800" :
                                                "bg-stone-100 text-stone-700"
                  }`}
                >
                  {c.rodden_rating}
                </span>
              )}
            </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ─── HUB VIEW (no category in URL) ──────────────────────────────────────────
function HubView() {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    api.get("/celebrities/categories").then(({ data }) => setCats(data.categories || []));
  }, []);

  return (
    <PageShell>
      <SEO
        title="AstroKuberChat Celebrity Data — 1,500+ Verified Birth Charts of Billionaires, Lottery Winners, Spiritual Gurus, Kings & Queens"
        description="Browse 1,500+ verified birth charts of the world's most influential people — billionaires (Steve Jobs, Elon Musk, Warren Buffett, Bezos, Gates, Ambani, Adani), AA-rated lottery winners (Edwin Castro $2.04B, Paula Moore $42M, Cynthia Stafford $112M), rags-to-riches icons (Chaplin, Carnegie, Madonna, Eminem, Stallone, J.K. Rowling, LeBron, Ronaldo), spiritual gurus (Osho, Ramana Maharshi, Vivekananda), reigning monarchs (Queen Elizabeth II, King Charles III, Louis XIV, Emperor Naruhito) and sudden-internet-famous icons. Each entry shows D1 + D9 + the exact Vimshottari Dasha they rose under, with mathematical explanation. Every chart sourced from verified historical records and tagged with its Rodden rating."
        path="/celebrities"
        keywords={[
          "celebrity birth chart", "celebrity kundali", "famous people astrology",
          "billionaire birth chart", "rags to riches astrology", "rags to riches kundali",
          "lottery winner birth chart", "lottery winner kundali", "edwin castro chart",
          "powerball winner astrology", "mega millions winner birth chart",
          "self made billionaire chart", "famous people dasha", "celebrity navamsa",
          "what dasha makes you rich", "what dasha do you win the lottery",
          "verified birth chart", "rodden rating", "AA rated chart",
          "steve jobs chart", "elon musk kundali", "eminem chart", "madonna chart",
          "charlie chaplin astrology", "andrew carnegie chart", "stallone chart",
          "leonardo dicaprio kundali", "lebron james chart", "cristiano ronaldo astrology",
          "j.k. rowling chart", "dhirubhai ambani kundali", "sam walton chart",
          "sudden wealth yoga", "dhana yoga", "rahu lottery", "jupiter uranus lottery",
          "queen elizabeth ii chart", "king charles iii chart", "louis xiv chart",
          "royal birth chart", "kings and queens astrology", "monarchy kundali",
        ]}
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-8 relative !z-50">
        <div className="flex items-center gap-2 label-eyebrow text-gold">
          <Crown size={14} weight="fill" /> AstroKuberChat Celebrity Data
          <span
            className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/40 text-gold text-[10px] font-bold tracking-wider normal-case"
            data-testid="celeb-count-chip"
          >
            <Lightning size={10} weight="fill" /> 1,500+ CHARTS
          </span>
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 font-light leading-tight">
          The birth charts of the world&apos;s <span className="italic text-gold">most influential people</span>.
        </h1>
        <p className="text-muted text-base sm:text-lg mt-5 max-w-3xl font-light leading-relaxed">
          Verified charts from <span className="text-gold">authoritative historical records</span> —
          every entry tagged with its original Rodden rating. Click any chart to see their
          D1 + D9 and the <span className="text-gold font-medium">Vimshottari Dasha that catapulted them</span>,
          with a mathematical breakdown of <em>why</em>.
        </p>
        <div className="mt-6 inline-flex flex-wrap items-center gap-3 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#FFB800]/12 to-transparent border border-[#FFB800]/30" data-testid="lifetime-pricing-banner">
          <Sparkle size={14} weight="fill" className="text-gold" />
          <span className="text-sm">
            <strong className="text-foreground">Free preview:</strong>
            <span className="text-muted"> 5 full charts (Steve Jobs · Eminem · Edwin Castro · Osho · Queen Elizabeth II) + every chart&apos;s D1 &amp; story.</span>
          </span>
          <span className="text-sm text-gold font-medium">Unlock all 1,500+ D9 + Dasha analyses — ₹15,000 lifetime.</span>
        </div>

        <Link to="/tools/celebrity-pattern-recognizer" className="mt-5 flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500/15 via-cyan-500/10 to-transparent border border-emerald-500/30 hover:border-emerald-500/60 transition-colors" data-testid="pattern-recognizer-banner">
          <Lightning size={16} weight="fill" className="text-emerald-500" />
          <span className="text-sm"><strong className="text-foreground">New: Celebrity Pattern Recognizer.</strong><span className="text-muted"> Ask "Where was Rahu in the Varshaphal when they won the lottery?" and see the pattern across a whole category.</span></span>
          <span className="text-sm text-emerald-600 font-medium inline-flex items-center gap-1">₹3,000 · 30 days <ArrowRight size={14} /></span>
        </Link>

        <CelebritySearchBar />
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="categories-hub">
        <div className="grid md:grid-cols-2 gap-6">
          {cats.map((cat) => {
            const Icon = CATEGORY_ICON[cat.icon] || Sparkle;
            const grad = CATEGORY_GRADIENT[cat.slug] || "from-[#FFB800] to-[#F97316]";
            return (
              <Link
                key={cat.slug}
                to={`/celebrities/${cat.slug}`}
                className="card-surface rounded-2xl overflow-hidden group hover:border-[#FFB800]/40 transition-all"
                data-testid={`category-card-${cat.slug}`}
              >
                <div className={`h-48 bg-gradient-to-br ${grad} relative overflow-hidden`}>
                  {/* subtle texture wash so the gradient never looks flat behind the faces */}
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.18),transparent_60%)] pointer-events-none" />

                  {/* Overlapping circular portraits — pulled from `hero_faces` */}
                  {(cat.hero_faces && cat.hero_faces.length > 0) && (
                    <div className="absolute left-6 top-1/2 -translate-y-1/2 flex items-center" data-testid={`category-faces-${cat.slug}`}>
                      {cat.hero_faces.slice(0, 3).map((src, idx) => (
                        <img
                          key={src}
                          src={src}
                          alt=""
                          loading="lazy"
                          className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover ring-4 ring-white/95 shadow-xl bg-white/20 transition-transform group-hover:scale-105"
                          style={{
                            marginLeft: idx === 0 ? 0 : "-18px",
                            zIndex: 10 - idx,
                            transform: `rotate(${idx === 0 ? -4 : idx === 2 ? 4 : 0}deg)`,
                            objectPosition: "center 20%",
                          }}
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ))}
                    </div>
                  )}

                  {/* Icon + count anchored to the right */}
                  <div className="absolute top-5 right-6">
                    <Icon size={32} weight="duotone" className="text-white/85 drop-shadow-lg" />
                  </div>
                  <span className="absolute bottom-3 right-6 font-serif-display text-6xl text-white/35 leading-none select-none">
                    {cat.count}
                  </span>
                </div>
                <div className="p-6">
                  <div className="font-serif-display text-3xl">{cat.label}</div>
                  <p className="text-muted text-sm mt-3 font-light leading-relaxed">
                    {cat.hero_blurb}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-2 text-gold text-sm font-medium group-hover:gap-3 transition-all">
                    Browse {cat.count} charts <ArrowRight size={14} weight="bold" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </PageShell>
  );
}

// ─── LIST VIEW (category in URL) ────────────────────────────────────────────
function ListView({ category }) {
  const [celebs, setCelebs] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needAuth, setNeedAuth] = useState(false);
  const [query, setQuery] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    setNeedAuth(false);
    api.get(`/celebrities/list/${category}`)
      .then(({ data }) => {
        setCelebs(data.celebrities || []);
        setMeta(data.category);
      })
      .catch((err) => {
        // 401 = members-only database (Oct 2026 anti-scrape policy)
        if (err?.response?.status === 401) setNeedAuth(true);
      })
      .finally(() => setLoading(false));
  }, [category]);

  if (needAuth) {
    return (
      <PageShell>
        <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="celeb-list-signin-wall">
          <div className="rounded-2xl bg-white shadow-xl border border-amber-300/60 p-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 text-amber-800 mb-3">
              <LockKey size={22} weight="duotone" />
            </div>
            <h2 className="font-serif-display text-2xl font-light text-[#3F2D1B]">Members-only database</h2>
            <p className="text-sm text-muted mt-2 font-light leading-relaxed">
              This celebrity collection is proprietary research. Sign in free to browse
              the full list and every chart inside it.
            </p>
            <button
              type="button"
              onClick={() => startGoogleSignIn(window.location.href)}
              className="btn-primary rounded-full w-full mt-5 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-2"
              data-testid="celeb-list-signin-btn"
            >
              <Sparkle size={14} weight="fill" /> Sign in free to browse
            </button>
            <Link to="/celebrities" className="block text-xs text-muted mt-3 underline underline-offset-2" data-testid="celeb-list-wall-back">
              ← Back to all categories
            </Link>
          </div>
        </div>
      </PageShell>
    );
  }

  const filtered = celebs.filter((c) => {
    if (ratingFilter !== "all" && c.rodden_rating !== ratingFilter) return false;
    if (query.trim() && !`${c.name} ${c.company} ${c.industry}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  const aaCount = celebs.filter((c) => c.rodden_rating === "AA").length;
  const aCount  = celebs.filter((c) => c.rodden_rating === "A").length;
  const Icon = meta?.icon ? (CATEGORY_ICON[meta.icon] || Sparkle) : Sparkle;

  const isBillionaires = category === "billionaires";
  const isLottery      = category === "lottery-winners";
  const isGuru         = category === "spiritual-gurus";
  const isRoyal        = category === "royals";
  const titleSEO = isBillionaires
    ? "30 Verified Billionaire Birth Charts — D1, D9 + Rise-Dasha Analysis"
    : isLottery
    ? "6 AA-Rated Lottery Winner Birth Charts — The Dasha That Triggered Their Jackpot"
    : isGuru
    ? "6 Spiritual Guru Birth Charts — The Dasha They Were Enlightened In"
    : isRoyal
    ? "8 Royal Birth Charts — The Vimshottari Dasha That Placed the Crown"
    : "15 Rags-to-Riches Birth Charts — From Welfare & Workhouses to Global Empires";

  const seoKeywords = isBillionaires ? [
    "billionaire birth chart", "billionaire kundali", "richest people astrology",
    "steve jobs chart", "elon musk kundali", "warren buffett chart", "bill gates kundali",
    "mukesh ambani chart", "gautam adani astrology", "jeff bezos kundali", "what dasha makes a billionaire",
  ] : isLottery ? [
    "lottery winner birth chart", "lottery winner kundali", "lottery winner astrology",
    "edwin castro chart", "edwin castro powerball astrology", "edwin castro birth time",
    "paula moore lottery", "robert abney verified record", "linda baldwin lottery winner",
    "cynthia stafford lottery birth chart", "michael dubin lottery",
    "what dasha do you win the lottery in", "rahu lottery winner", "jupiter uranus lottery",
    "sudden wealth astrology", "windfall horoscope", "lottery jackpot kundali",
    "2nd house lottery", "5th house lottery", "8th house sudden wealth",
    "11th house gains", "lottery winning yoga", "dhana yoga lottery",
    "powerball winner birth chart", "mega millions winner astrology",
  ] : isGuru ? [
    "spiritual guru birth chart", "guru kundali", "enlightenment astrology",
    "osho birth chart", "rajneesh kundali", "bhagwan shree rajneesh astrology",
    "osho enlightenment dasha", "ramana maharshi birth chart", "ramana maharshi kundali",
    "sri aurobindo chart", "siddhi day astrology", "krishnamurti birth chart",
    "krishnamurti the process ojai astrology", "vivekananda kundali", "meher baba birth chart",
    "what dasha gives enlightenment", "ketu enlightenment dasha", "moksha dasha",
    "moksha yoga astrology", "kundalini awakening chart", "samadhi dasha",
    "12th house moksha", "9th house dharma", "ketu spirituality", "saturn renunciation",
    "vedic astrology spiritual awakening", "guru kundali analysis",
  ] : isRoyal ? [
    "royal birth chart", "king kundali", "queen birth chart", "monarchy astrology",
    "queen elizabeth ii birth chart", "queen elizabeth astrology", "elizabeth ii kundali",
    "king charles iii birth chart", "charles iii astrology",
    "queen victoria chart", "george vi chart", "louis xiv sun king chart",
    "emperor naruhito chart", "king felipe vi spain chart",
    "king willem alexander netherlands chart",
    "coronation astrology", "accession dasha", "throne dasha",
    "what dasha makes you king", "10th house throne", "sun karaka monarchy",
    "jupiter divine right", "raja yoga king kundali", "raja yoga monarchy",
    "monarchy vedic astrology", "royal house astrology", "royal succession astrology",
  ] : [
    "rags to riches astrology", "rags to riches kundali", "self made success chart",
    "from poverty to wealth astrology", "charlie chaplin chart", "andrew carnegie kundali",
    "madonna birth chart", "eminem chart", "stallone kundali", "j.k. rowling chart",
    "leonardo dicaprio astrology", "lebron james chart", "cristiano ronaldo kundali",
    "dhirubhai ambani astrology", "tyler perry chart", "whoopi goldberg birth chart",
    "what dasha takes you from poverty to fame",
  ];

  return (
    <PageShell>
      <SEO
        title={`AstroKuberChat Celebrity Data · ${titleSEO}`}
        description={meta?.hero_blurb || ""}
        path={`/celebrities/${category}`}
        keywords={seoKeywords}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": `AstroKuberChat · ${meta?.label || "Celebrities"}`,
          "description": meta?.hero_blurb || "",
          "url": `https://astrokuberchat.com/celebrities/${category}`,
          "mainEntity": {
            "@type": "ItemList",
            "numberOfItems": celebs.length,
            "itemListElement": celebs.slice(0, 40).map((c, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "url": `https://astrokuberchat.com/celebrities/${category}/${c.slug}`,
              "name": c.name,
            })),
          },
        }}
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-8">
        <Link to="/celebrities" className="text-muted hover:text-gold text-sm inline-flex items-center gap-1.5 mb-6" data-testid="back-to-hub">
          ← All categories
        </Link>
        <div className="flex items-center gap-2 label-eyebrow text-gold">
          <Icon size={14} weight="fill" /> {meta?.label || "Celebrities"}
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 font-light leading-tight max-w-4xl">
          {meta?.hero_title || "Loading…"}
        </h1>
        {meta?.hero_blurb && (
          <p className="text-muted text-base sm:text-lg mt-5 max-w-3xl font-light leading-relaxed">
            {meta.hero_blurb}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-4 mt-8 text-xs label-eyebrow text-muted">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span> AA · {aaCount}
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-300">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400"></span> A · {aCount}
          </span>
          <span className="text-muted normal-case tracking-normal">
            All charts computed live via Swiss Ephemeris · 100% free, no signup
          </span>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-2 mb-8">
        <div className="card-surface rounded-xl p-4 sm:p-5 flex flex-wrap items-center gap-3" data-testid="celebrities-filters">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, company, industry…"
              className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-bg/60 border border-soft text-sm focus:outline-none focus:border-[#FFB800]/60"
              data-testid="celebrities-search"
            />
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            {[
              ["all", "All", celebs.length],
              ["AA",  "AA",  aaCount],
              ["A",   "A",   aCount],
            ].map(([k, label, count]) => (
              <button
                key={k}
                onClick={() => setRatingFilter(k)}
                className={`px-3 py-1.5 rounded-full border transition-all ${
                  ratingFilter === k
                    ? "bg-[#FFB800] border-[#FFB800] text-[#1A1410] font-medium"
                    : "bg-transparent border-soft text-muted hover:border-[#FFB800]/40"
                }`}
                data-testid={`rating-filter-${k}`}
              >
                {label} · {count}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="celebrities-grid">
        {loading ? (
          <div className="text-center py-16 text-muted">Loading celebrity charts…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted">No charts match your filter.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((c) => {
              const rs = RATING_STYLE[c.rodden_rating] || RATING_STYLE.X;
              return (
                <Link
                  key={c.slug}
                  to={`/celebrities/${category}/${c.slug}`}
                  className="card-surface rounded-xl overflow-hidden group hover:border-[#FFB800]/40 transition-all"
                  data-testid={`celebrity-card-${c.slug}`}
                >
                  <div className="relative aspect-[4/5] overflow-hidden bg-[#FAF4E8]">
                    <CelebrityAvatar name={c.name} className="w-full h-full absolute inset-0" size="100%" fontSize={64} />
                    {c.photo && (
                      <img
                        src={c.photo}
                        alt={c.name}
                        loading="lazy"
                        className="w-full h-full object-cover object-[center_25%] group-hover:scale-105 transition-transform duration-500 relative z-10"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent pointer-events-none z-20" />
                    <div className={`absolute top-3 right-3 px-2 py-0.5 text-[10px] font-medium rounded-full border ${rs.bg} ${rs.text} ${rs.border} backdrop-blur-sm bg-black/30 z-30`}>
                      {c.rodden_rating}
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="font-serif-display text-xl">{c.name}</div>
                    <div className="text-muted text-xs mt-1">{c.industry}</div>
                    {c.rags_to_riches_story && (
                      <div className="text-[11px] text-muted mt-2 normal-case tracking-normal leading-relaxed line-clamp-2">
                        {c.rags_to_riches_story}
                      </div>
                    )}
                    {c.lottery_story && (
                      <div className="text-[11px] text-muted mt-2 normal-case tracking-normal leading-relaxed line-clamp-2">
                        {c.lottery_story}
                      </div>
                    )}
                    {c.guru_story && (
                      <div className="text-[11px] text-muted mt-2 normal-case tracking-normal leading-relaxed line-clamp-2">
                        {c.guru_story}
                      </div>
                    )}
                    {c.royal_story && (
                      <div className="text-[11px] text-muted mt-2 normal-case tracking-normal leading-relaxed line-clamp-2">
                        {c.royal_story}
                      </div>
                    )}
                    <div className="text-gold text-xs mt-3 flex items-center gap-1.5">
                      <Sparkle size={11} weight="fill" />
                      Rose to fame · {c.rise_year}
                    </div>
                    <div className="text-xs text-muted mt-2 normal-case tracking-normal">
                      {c.company}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </PageShell>
  );
}

// ─── ROOT (route picker) ────────────────────────────────────────────────────
export default function CelebritiesPage() {
  const { category } = useParams();
  if (!category) return <HubView />;
  return <ListView category={category} />;
}
